import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Container from "@/components/Container";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import { api } from "@/lib/api";
import { ArrowLeft } from "lucide-react";

export default function CaseStudyDetail() {
  const { id } = useParams();
  const [s, setS] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    api.get(`/public/case-studies/${id}`).then(r => setS(r.data)).catch(() => setErr(true));
  }, [id]);
  if (err) return <Container className="pt-40"><h1 className="serif-display text-5xl text-ink-700">Case study not found</h1><Link to="/before-after" className="btn-link mt-8 inline-flex">← Back</Link></Container>;
  if (!s) return <Container className="pt-40 text-ink-100">Loading…</Container>;
  const pairs = (s.slider_pairs?.length ? s.slider_pairs : null) ||
    (s.before_images?.length && s.after_images?.length
      ? s.before_images.map((b, i) => ({ before: b, after: s.after_images[i] || s.after_images[0] }))
      : []);
  return (
    <div className="pt-32 pb-32">
      <Container className="pb-12">
        <Link to="/before-after" className="inline-flex items-center gap-2 text-sm text-ink-400 hover:text-ruby"><ArrowLeft size={14}/> Transformations</Link>
        <div className="mt-8 flex items-center gap-4">
          <span className="label-mono">{s.location}</span>
          <span className="h-px w-10 bg-ruby" />
        </div>
        <h1 className="mt-6 serif-display text-5xl md:text-7xl lg:text-[7rem] text-ink-700">{s.project_title}</h1>
      </Container>
      <Container className="space-y-16">
        {pairs.length === 0 && s.main_image && <img src={s.main_image} alt="" className="w-full aspect-[16/9] object-cover" />}
        {pairs.map((p, i) => <BeforeAfterSlider key={i} before={p.before} after={p.after} labelBefore={p.label || `Before · ${i+1}`} labelAfter="After" tone="light" />)}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 lg:gap-16">
          <div className="md:col-span-2">
            <p className="label-mono">The Story</p>
            <p className="mt-5 text-ink-400 leading-[1.85] whitespace-pre-line text-base lg:text-lg">{s.short_story}</p>
            <p className="label-mono mt-14">Design Approach</p>
            <p className="mt-5 text-ink-400 leading-[1.85] whitespace-pre-line text-base lg:text-lg">{s.design_approach}</p>
          </div>
          <div>
            <p className="label-mono">Work Completed</p>
            <ul className="mt-5 space-y-3">
              {(s.work_completed || []).map((w, i) => <li key={i} className="flex gap-3 serif-light text-ink-700 text-lg"><span className="editorial-numeral">{String(i+1).padStart(2,"0")}</span>{w}</li>)}
            </ul>
            {(s.show_purchase_price || s.show_renovation_cost || s.show_sold_price || s.show_profit) && (
              <div className="surface-cream p-6 mt-10 border-t-2 border-ruby">
                <p className="label-mono">Project Finance</p>
                <dl className="mt-4 space-y-3 text-sm">
                  {s.show_purchase_price && <Row k="Purchase" v={`£${(s.purchase_price||0).toLocaleString()}`} />}
                  {s.show_renovation_cost && <Row k="Renovation" v={`£${(s.renovation_cost||0).toLocaleString()}`} />}
                  {s.show_sold_price && <Row k="Sold" v={`£${(s.sold_price||0).toLocaleString()}`} />}
                  {s.show_profit && <Row k="Profit" v={`£${(s.profit||0).toLocaleString()}`} />}
                </dl>
              </div>
            )}
          </div>
        </div>
      </Container>
    </div>
  );
}

function Row({ k, v }) {
  return <div className="flex justify-between border-b border-ink-700/15 pb-2"><dt className="label-mono-mute">{k}</dt><dd className="serif-light text-base text-ink-700">{v}</dd></div>;
}
